import moment from "moment"
import { TypeClassSession } from "constant/TypeClassSession"

export const colorMappings = {
  lesson: (item) => {
    const { startTime, endTime } = item
    if (moment().isAfter(moment(endTime))) return "grey"
    if (moment().isBefore(moment(startTime), "day")) return "#BCD9F2"
    if (moment().isSame(moment(startTime), "day")) return "#2576B9"
    return "#FFEBDA"
  },
  test: (item) => {
    const { startTime, endTime } = item
    if (moment().isAfter(moment(endTime))) return "grey"
    if (moment().isSameOrBefore(moment(startTime), "day")) return "#F4CE14"
    return "#FFEBDA"
  },
  ptDemo: (item) => {
    const { startTime, endTime } = item
    if (moment().isAfter(moment(endTime))) return "grey"
    if (moment().isSameOrBefore(moment(startTime), "day")) return "#EABEFF"
    return "#FFEBDA"
  },
  feedback: (item) => {
    const { deadlineDate, feedBackTrackingId } = item
    if (moment().isAfter(moment(deadlineDate)))
      return feedBackTrackingId === 4 || feedBackTrackingId === 2
        ? "red"
        : "grey"
    if (moment().isSameOrBefore(moment(deadlineDate), "day")) return "#0DCAF0"
    return "#FFEBDA"
  },
  report: (item) => {
    const { deadline, gradeBookDue, status } = item
    const time = deadline || gradeBookDue
    if (moment().isAfter(moment(time)))
      return status === "OVERDUE" ? "red" : "grey"
    if (moment().isSameOrBefore(moment(time), "day")) return "springgreen"
    return "#FFEBDA"
  },
  workshop: (item) => {
    const { deadline, status } = item
    if (moment().isAfter(moment(deadline)))
      return status === "OVERDUE" ? "red" : "grey"
    if (moment().isSameOrBefore(moment(deadline), "day")) return "#FFAEA5"
    return "#FFEBDA"
  },
  studentAtRisk: () => "orange",
  eventEOCCAndTestDTO: () => "#F4CE14",
  default: () => "white",
  eventTaskDTO: () => "#FFA500",
}

export const typeMapping = {
  classSessionScheduleList: "Lesson",
  testEOCCList: "EOCC & Test",
  ptDemoList: "PT/Demo",
  feedback: "Feedback",
  dailyReports: "Daily Report",
  progressReports: "Progress Report",
  gradeBookReports: "Gradebook Report",
  listCalendarWorkShop: "Training/Workshop",
  // listCalendarWorkShop: "TRAINING/WORKSHOP",
  studentAtRiskTask: TypeClassSession.STUDENTS_AT_RISK,
}

export const keyToColorMapping = {
  classSessionScheduleList: colorMappings.lesson,
  testEOCCList: colorMappings.test,
  ptDemoList: colorMappings.ptDemo,
  feedback: colorMappings.feedback,
  dailyReports: colorMappings.report,
  progressReports: colorMappings.report,
  gradeBookReports: colorMappings.report,
  listCalendarWorkShop: colorMappings.workshop,
  studentAtRiskTask: colorMappings.studentAtRisk,
  eventLessonDTO: colorMappings.lesson,
  eventPTDemoDTO: colorMappings.ptDemo,
  eventTaskDTO: colorMappings.eventTaskDTO,
  eventFeedbackDTO: colorMappings.feedback,
  eventDailyReportDTO: colorMappings.report,
  eventEOCCAndTestDTO: colorMappings.eventEOCCAndTestDTO,
  trainingWorkShopDTO: colorMappings.workshop,
  eventDTO: colorMappings.feedback,
}

export const convertTime = (time, diff = 0, timezone = 0) =>
  moment(time)
    .add(-7, "hours")
    .add(diff ?? 0, "minutes")
    .add(timezone, "hours")
    .format("YYYY-MM-DD HH:mm:ss")

export const mapTab = (item, key) => {
  const type = typeMapping[key] || ""
  const getColor = keyToColorMapping[key] || colorMappings.default
  const color = getColor(item)

  return { type, color }
}

export const mapTitle = (item, key) => {
  switch (key) {
    case "classSessionScheduleList":
    case "testEOCCList":
    case "dailyReports":
    case "gradeBookReports":
    case "progressReports":
      return item.className || "Untitled"
    case "ptDemoList":
      return item.eventName || item.eventTypeName || "Untitled"
    case "feedback":
      return item.feedBackCode || "Untitled"
    case "studentAtRiskTask":
      return item.class_name || item.className || "Untitled" // Use class_name as fallback
    case "listCalendarWorkShop":
      return item.eventTypeName || "Untitled"
    case "eventLessonDTO":
      return item.className || "Untitled"
    case "eventPTDemoDTO":
      return item.eventName || item.center || "Untitled"
    case "eventTaskDTO":
      return item.eventName || "Untitled"
    case "eventEOCCAndTestDTO":
      return item.className || "Untitled"
    case "trainingWorkShopDTO":
      return item.eventName || "Untitled"
    case "eventDTO":
      return item.name || "Untitled"
    default:
      return "Untitled"
  }
}

export const mapTime = (item, key, timezone = 0) => {
  switch (key) {
    case "feedback":
      return {
        start: convertTime(item.deadlineDate, 0, timezone),
        end: convertTime(item.deadlineDate, 1, timezone),
      }
    case "dailyReports":
    case "progressReports":
    case "gradeBookReports":
      const time = item.deadline || item.gradeBookDue
      return {
        start: convertTime(time, 0, timezone),
        end: convertTime(time, 30, timezone),
      }
    default:
      return {
        start: convertTime(item.startTime, 0, timezone),
        end: convertTime(item.endTime, 0, timezone),
      }
  }
}

export const mapDeadline = (item, key, timezone = 0) => {
  const time = item.deadlineDate || item.deadline || item.gradeBookDue
  return convertTime(time, 0, timezone)
}

export const shouldFilterOutEvent = (tabValue, event) => {
  if (tabValue === "task" && event.type === "Lesson") return true
  return false // Allow all events for "All" tab
}

export const handleGroupedEvents = (acc, key, item, timezone = 0) => {
  const title = mapTitle(item, key)
  // console.log(`Mapping title for ${key}:`, title); // Debug log
  const time = mapTime(item, key, timezone)

  const colorInfo = mapTab(item, key)
  const deadline = mapDeadline(item, key, timezone)

  if (key === "studentAtRiskTask") {
    const className = item.className
    const existing = acc.find(
      (event) => event.extendedProps?.studentAtRiskClassName === className
    )
    if (!existing) {
      acc.push({
        title,
        start: convertTime(item.startTime, 0, timezone), // Use end_date as a fallback
        end: convertTime(item.endTime, 0, timezone),
        backgroundColor: colorInfo.color,
        extendedProps: {
          data: [item],
          deadline,
          studentAtRiskClassName: className,
        },
        type: colorInfo.type,
      })
    } else {
      if (!Array.isArray(existing.extendedProps.data)) {
        existing.extendedProps.data = [existing.extendedProps.data]
      }
      existing.extendedProps.data.push(item)
    }
  } else if (key === "gradeBookReports" || key === "progressReports") {
    const className = item.className
    const propKey =
      key === "gradeBookReports"
        ? "gradeBookReportsClassName"
        : "progressReportsClassName"
    const existing = acc.find((e) => e.extendedProps?.[propKey] === className)
    if (!existing) {
      acc.push({
        title,
        ...time,
        backgroundColor: colorInfo.color,
        extendedProps: {
          data: [item],
          deadline,
          [propKey]: className,
        },
        type: colorInfo.type,
      })
    } else {
      // Ensure existing.extendedProps.data is an array
      if (!Array.isArray(existing.extendedProps.data)) {
        existing.extendedProps.data = [existing.extendedProps.data]
      }
      existing.extendedProps.data.push(item)
    }
  } else if (key === "listCalendarWorkShop") {
    acc.push({
      title: item.eventTypeName,
      ...time,
      backgroundColor: colorInfo.color,
      extendedProps: {
        data: item,
        deadline: item.deadline,
        type: "Training/Workshop",
      },
      type: colorInfo.type,
    })
  } else if (key === "eventDTO") {
    acc.push({
      title,
      ...time,
      backgroundColor: colorInfo.color,
      extendedProps: {
        data: item,
        deadline: item.endTime,
      },
      type: "Event",
    })
  } else {
    acc.push({
      title,
      ...time,
      backgroundColor: colorInfo.color,
      extendedProps: {
        data: item,
        deadline: item?.endTime || deadline,
      },
      type: colorInfo.type || key,
    })
  }

  return acc
}

export const renderModalContentByType = (data) => {
  const type = data?.extendedProps?.type || data?.extendedProps?.data?.eventType
  const item = data?.extendedProps?.data
  const startTime = data?.start
    ? moment(data.start).add(-7, "h").format("DD-MM-YYYY HH:mm")
    : ""
  const endTime = data?.end
    ? moment(data.end).add(-7, "h").format("DD-MM-YYYY HH:mm")
    : data?.extendedProps?.data?.endTime
    ? moment(data?.extendedProps?.data?.endTime)
        .add(-7, "h")
        .format("DD-MM-YYYY HH:mm")
    : ""

  switch (type) {
    case "Lesson":
    case "LESSON":
    case "eventLessonDTO":
      return [
        { label: "Class name", value: item?.className },
        { label: "Center name", value: item?.centerName || item?.center },
        { label: "Start time", value: startTime },
        { label: "End time", value: endTime },
      ]

    case "TASK":
    case "Event":
      return [
        { label: "Category", value: item?.category },
        { label: "Name", value: item?.name || item?.center },
        { label: "Description", value: item?.description || item?.center },
        { label: "Location", value: item?.location || item?.center },
        { label: "Start time", value: startTime },
        { label: "End time", value: endTime },
      ]

    case "PT/Demo":
    case "PT/DEMO":
      return [
        { label: "Center", value: item?.centerName },
        { label: "Event", value: item?.eventName },
        { label: "Event Type", value: item?.eventTypeName },
        { label: "Teacher", value: item?.fullNameEN },
        { label: "Start time", value: startTime },
        { label: "End time", value: endTime },
        { label: "Number of appointments", value: item?.appointment },
      ]

    case "Feedback":
      return [
        { label: "Feedback subject", value: item?.feedBackSubject },
        { label: "Start time", value: startTime },
        { label: "End time", value: endTime },
      ]

    case "Daily Report":
      return [
        { label: "Session name", value: item?.sessionName },
        { label: "Start time", value: startTime },
        { label: "End time", value: endTime },
      ]

    case "Progress Report":
      return [
        {
          label: "Class name",
          value: data?.extendedProps?.progressReportsClassName,
        },
        {
          label: "Number of reports",
          value: data?.extendedProps?.data?.length,
        },
        { label: "Start time", value: startTime },
        { label: "End time", value: endTime },
      ]

    case "Gradebook Report":
      return [
        {
          label: "Class name",
          value: data?.extendedProps?.gradeBookReportsClassName,
        },
        {
          label: "Number of reports",
          value: data?.extendedProps?.data?.length,
        },
        { label: "Start time", value: startTime },
        { label: "End time", value: endTime },
      ]

    case "EOCC & Test":
    case "EOCC/TEST":
      return [
        { label: "Event", value: item?.event },
        { label: "Start time", value: startTime },
        { label: "End time", value: endTime },
      ]

    case "eventTaskDTO":
    case "eventPTDemoDTO":
      return [
        { label: "Event", value: item?.eventName },
        { label: "Event Type", value: item?.eventType },
        { label: "Start time", value: startTime },
        { label: "End time", value: endTime },
      ]

    default:
      return [
        { label: "Start time", value: startTime },
        { label: "End time", value: endTime },
      ]
  }
}

export const getModalButtons = (type, data, actions) => {
  const { onClose, onLmsClick, onHubClick, onNavigate } = actions

  const baseButtons = [
    {
      text:
        type === "Lesson" || type === "PT/Demo" || type === "EOCC & Test"
          ? "OK"
          : "Go to To-do list",
      onClick: () => {
        if (
          type === "Lesson" ||
          type === "PT/Demo" ||
          type === "EOCC & Test" ||
          type === "Training"
        ) {
          onClose()
          return
        }
        onNavigate(type, data)
      },
    },
  ]

  if (type === "Lesson") {
    baseButtons.push({
      text: "GO TO LMS",
      onClick: onLmsClick,
    })
  } else if (type === "PT/Demo") {
    baseButtons.push({
      text: "GO TO HUB",
      onClick: onHubClick,
    })
  } else if (type !== "EOCC & Test" && type !== "Training") {
    baseButtons.push({
      text: "OK",
      onClick: onClose,
    })
  }

  return baseButtons
}

export const getNavigationParams = (type, data) => {
  const item = data?.extendedProps?.data
  let itemNew = item
  if (item && item.length) {
    itemNew = item[0]
  }

  const idMap = {
    Feedback: itemNew?.feedback?.feedBackId,
    "Students at Risk": itemNew?.id,
    "Daily Report": itemNew?.dailyReportId,
    "Progress Report": itemNew?.progressReportId,
    "Gradebook Report": itemNew?.gradeBookReportId,
  }

  return {
    id: idMap[type] || item?.eventId || null,
    start: data?.start
      ? moment(data.start).add(-7, "h").format("MM-DD-YYYY")
      : "",
    end: data?.end ? moment(data.end).add(-7, "h").format("MM-DD-YYYY") : "",
    type,
  }
}
